import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuthStore } from '../../store/authStore';
import { storeRedirectIntent } from '../../utils/authRedirect';
import GlobalNavBar from '../layout/GlobalNavBar';
import { useTranslation } from 'react-i18next';

interface ConfirmEmailFormData {
  email: string;
  confirmationCode: string;
}

export const ConfirmEmailForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const confirmEmail = useAuthStore((state) => state.confirmEmail);
  const resendVerificationCode = useAuthStore((state) => state.resendVerificationCode);
  const { t } = useTranslation('auth');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ConfirmEmailFormData>({
    defaultValues: {
      email: location.state?.email || '',
    },
  });

  const onSubmit = async (data: ConfirmEmailFormData) => {
    setIsLoading(true);
    try {
      await confirmEmail(data.email, data.confirmationCode);
      
      // Check if there's a stored redirect path from registration
      const redirectPath = location.state?.redirectPath;
      
      if (redirectPath) {
        // Store it for after login using the validated utility
        storeRedirectIntent(redirectPath);
      }
      
      navigate('/login');
    } catch (error) {
      console.error('Email confirmation error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    // Try to get email from location state first, then from form
    const emailFromState = location.state?.email;
    const emailFromForm = watch('email');
    const email = emailFromState || emailFromForm;
    
    if (!email) {
      console.error('No email found');
      return;
    }

    setIsResending(true);
    try {
      await resendVerificationCode(email);
    } catch (error) {
      console.error('Resend error:', error);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNavBar variant="dark" />
      <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('auth:confirmYourEmail')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {location.state?.message || t('auth:enterConfirmationCode')}
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('auth:emailAddress')}
              </label>
              <input
                {...register('email', {
                  required: t('auth:errors.emailRequired'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('auth:errors.invalidEmail'),
                  },
                })}
                type="email"
                className="mt-1 input"
                placeholder={t('auth:enterEmail')}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmationCode" className="block text-sm font-medium text-gray-700">
                {t('auth:confirmationCode')}
              </label>
              <input
                {...register('confirmationCode', {
                  required: t('auth:errors.confirmationCodeRequired'),
                  minLength: {
                    value: 6,
                    message: t('auth:errors.confirmationCodeMinLength'),
                  },
                })}
                type="text"
                className="mt-1 input"
                placeholder={t('auth:enter6DigitCode')}
                maxLength={6}
              />
              {errors.confirmationCode && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmationCode.message}</p>
              )}
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? t('auth:confirming') : t('auth:confirmEmail')}
            </button>
          </div>

          <div className="mt-6 text-center space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-2">
                {t('auth:didntReceiveCode', 'Didn\'t receive the code?')}
              </p>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={isResending}
                className="text-blue-600 hover:text-blue-700 font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {isResending ? t('auth:sending', 'Sending...') : t('auth:resendCode', 'Resend Verification Code')}
              </button>
            </div>

            <div>
              <Link
                to="/login"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                {t('auth:backToSignIn')}
              </Link>
            </div>
          </div>
        </form>
      </div>
      </div>
    </div>
  );
};
